<template>
  <div
    class="py-4 px-4 h-100"
  >
    <template>
      <section
        class="mb-3"
      >
        <b-row>
          <b-col>
            <base-help-tooltip
              popover-placement="bottom"
            >
              <template v-slot:left>
                <div>
                  <h1 class="page-title">
                    Creative Performance
                  </h1>
                </div>
              </template>
              <template v-slot:popover-body>
                <p
                  class="mb-0"
                >
                  View the creative performance including the engagement data.
                </p>
              </template>
            </base-help-tooltip>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row class="mt-6">
          <b-col>
            <ReportStatements
              :type="'CAMPAIGN_PERFORMANCE'"
              v-if="$route.name !== 'MultipleReportingIndex'"
            />
          </b-col>
        </b-row>
      </section>
      <Filters
        @updateReport="updateReport"
        @setPageLoading="setPageLoading"
        @updateDates="updateDates"
        @areAllParametersPresent="areAllParametersPresent"
      />
      <div
        v-if="isPageLoading"
        class="loader mx-auto h-100"
      />
      <div v-else>
        <section>
          <b-row>
            <b-col>
              <b-card
                no-body
              >
                <b-card-body
                  class="pb-0"
                  style="min-height: calc(28rem);"
                >
                  <div
                    v-if="!allParametersArePresent"
                  >
                    <b-alert
                      show
                      variant="warning"
                      class="w-50 mt-5 mx-auto text-center"
                    >
                      There's no data to show
                    </b-alert>
                  </div>
                  <div v-else>
                    <div
                      v-if="!eventPerformanceChartData.loaded"
                      class="loader mx-auto my-5"
                    />
                    <event-performance-chart
                      v-else
                      :data="eventPerformanceChartData"
                      :has-scrolls="hasScrollsData"
                    />
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </section>
        <section>
          <b-row>
            <b-col
              cols="12"
            >
              <b-card
                class="mb-5"
              >
                <div
                  class="table-responsive"
                  v-if="allParametersArePresent"
                >
                  <b-table
                    class="card-table table-vcenter text-nowrap"
                    tbody-tr-class="creative-performance-table-row"
                    :striped="true"
                    :hover="true"
                    :items="overallCreativeCampaignData"
                    :fields="returnHeaders"
                    :busy="isCampaignPerformanceTableBusy"
                    show-empty
                    data-test="creative-performance-table"
                    sort-by="name"
                  >
                    <template
                      v-slot:cell(creative)="{item}"
                    >
                      {{ item.sizeName }}
                    </template>
                    <template
                      v-slot:cell(impressions)="{item}"
                    >
                      {{ toLocaleString(item.impressions) }}
                    </template>
                    <template
                      v-slot:cell(clicks)="{item}"
                    >
                      {{ toLocaleString(item.clicks) }}
                    </template>
                    <template
                      v-slot:cell(ctr)="{item}"
                    >
                      {{ toLocaleString(item.ctr, true) + "%" }}
                    </template>
                    <template
                      v-slot:cell(scrolls)="{item}"
                    >
                      {{ isValidNumber(item.scrolls) ? toLocaleString(item.scrolls) : "N/A" }}
                    </template>
                    <template
                      v-slot:cell(str)="{item}"
                    >
                      {{ isValidNumber(item.str) ? toLocaleString(item.str, true) + "%" : "N/A" }}
                    </template>
                    <template
                      #table-busy
                      style="min-height: calc(16rem);"
                    >
                      <div class="loader mx-auto" />
                    </template>
                  </b-table>
                </div>
                <div v-else>
                  <b-alert
                    show
                    variant="warning"
                    class="w-50 mt-5 mx-auto text-center"
                  >
                    There's no data to show
                  </b-alert>
                </div>
              </b-card>
            </b-col>
          </b-row>
        </section>
      </div>
    </template>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

import helper from '@/core/helper';
import EventPerformanceChart from '@/modules/reporting/components/CreativePerformance/EventPerformanceChart.vue';
import Filters from '@/modules/reporting/components/Filters.vue';
import ReportStatements from '@/modules/reporting/components/ReportStatements.vue';
import numbersHelper from '@/core/mixins/numbersHelper';
import dateHelper from '@/core/mixins/dateHelper';
import fileHelper from '@/core/mixins/fileHelper';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

export default {
  name: 'CreativePerformance',
  components: {
    BaseHelpTooltip,
    EventPerformanceChart,
    ReportStatements,
    Filters,
  },
  mixins: [
    numbersHelper,
    dateHelper,
    fileHelper,
  ],
  data() {
    return {
      isPageLoading: false,
      campaignList: [],
      campaignPublishersList: [],
      campaignContentItems: [],
      selectedCampaignIds: [],
      selectedPublishersIds: [],
      selectedCreativesIds: [],
      selectedContentItemIds: [],
      dateRange: {
        start: -1,
        end: -1,
      },
      dailyStats: [],
      eventPerformanceChartData: {
        loaded: false,
        labels: [],
        clicks: [],
        ctr: [],
        scrolls: [],
        str: [],
      },
      overallCreativeCampaignData: [],
      allParametersArePresent: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_ORDERS_START_DATE,
      campaignTypes.GET_ORDERS_END_DATE,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ACTIVE_CREATIVES,
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    hasScrollsData() {
      return this.dailyStats.some((stats) => stats.scrolls != null);
    },
    returnHeaders() {
      const headersWithScroll = [
        'creative',
        'impressions',
        'clicks',
        { key: 'ctr', label: 'ctr%' },
        'scrolls',
        { key: 'str', label: 'str%' },
      ];
      const headersWithNoScroll = [
        'creative',
        'impressions',
        'clicks',
        { key: 'ctr', label: 'ctr%' },
      ];
      return this.hasScrollsData ? headersWithScroll : headersWithNoScroll;
    },
    isCampaignPerformanceTableBusy() {
      return !this.overallCreativeCampaignData.lengh
        && (!this.eventPerformanceChartData.loaded);
    },
    startDateForReport() {
      return this.dateRange?.start == null || this.dateRange.start === -1
        ? this.GET_ORDERS_START_DATE : this.dateRange.start;
    },
    endDateForReport() {
      return this.dateRange?.end == null || this.dateRange.end === -1
        ? this.GET_ORDERS_END_DATE : this.dateRange.end;
    },
  },
  methods: {
    ...mapActions('reporting', [
      reportingTypes.FETCH_DAILY_STATS,
      reportingTypes.FETCH_OVERALL_CREATIVE_CAMPAIGN_STATS,
    ]),
    updatedSelectedOptions(selectedOptionsList, id, isChecked) {
      isChecked ? this[selectedOptionsList].push(id)
        : this[selectedOptionsList].splice(this[selectedOptionsList].indexOf(id), 1);
      this.updateReport();
    },
    selectAllOptions(fullList, selectedOptionsList, keyProperty) {
      this[selectedOptionsList] = this[fullList].map((option) => option[keyProperty]);
      this.updateReport();
    },
    deselectAllOptions(selectedOptionsList) {
      this[selectedOptionsList] = [];
      this.eventPerformanceChartData.loaded = false;
    },
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.dateRange.end = endDate;
      this.updateReport();
    },
    updateReport: helper.debounce(function fetchData(data) {
      if (this.allParametersArePresent) {
        this.selectedCampaignIds = data.campaignIds;
        this.selectedPublishersIds = data.publisherIds;
        this.selectedCreativesIds = data.creativeIds;
        this.selectedContentItemIds = data.contentItemIds;
        this.fetchPublisherData();
        this.fetchOverallCreativeCampaignData();
      }
    }, 1500),
    resetEventPerformanceChart() {
      this.eventPerformanceChartData = {
        loaded: false,
        labels: [],
        clicks: [],
        ctr: [],
        scrolls: [],
        str: [],
      };
    },
    populateEventPerformanceChartData(stats) {
      for (let i = 0; i < stats.length; i++) {
        this.eventPerformanceChartData.labels.push(this.formatDate(stats[i].date, 'dd/MM'));
        this.eventPerformanceChartData.clicks.push(stats[i].clicks);
        this.eventPerformanceChartData.ctr.push(stats[i].ctr);
        if (this.hasScrollsData) {
          this.eventPerformanceChartData.scrolls.push(stats[i].scrolls);
          this.eventPerformanceChartData.str.push(stats[i].str);
        }
      }
    },
    async fetchPublisherData() {
      this.dailyStats = [];
      this.resetEventPerformanceChart();

      const payload = {
        campaignIds: this.selectedCampaignIds,
        body: {
          fromDate: this.startDateForReport,
          toDate: this.endDateForReport,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
        },
      };
      const response = await this.FETCH_DAILY_STATS(payload);
      this.dailyStats = response.dailyStats.sort((stat1, stat2) => {
        if (stat1.date === stat2.date) return 0;
        return new Date(stat1.date) > new Date(stat2.date) ? 1 : -1;
      });
      this.populateEventPerformanceChartData(this.dailyStats);
      this.eventPerformanceChartData.loaded = true;
    },
    fetchOverallCreativeCampaignData() {
      const { advertiserId } = this.$route.params;
      const payload = {
        advertiserId,
        body: {
          fromDate: this.startDateForReport,
          toDate: this.endDateForReport,
          campaignIds: this.selectedCampaignIds,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
        },
      };

      this.FETCH_OVERALL_CREATIVE_CAMPAIGN_STATS(payload)
        .then((response) => {
          this.overallCreativeCampaignData.splice(0, this.overallCreativeCampaignData.length, ...response);
        });
    },
    setPageLoading(value) {
      this.isPageLoading = value;
      this.allParametersArePresent = value;
    },
    areAllParametersPresent(value) {
      this.allParametersArePresent = value;
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep {
  .card-table thead tr th:not(:last-child),
  .card-table tbody tr td:not(:last-child) {
    border-right: 1px solid rgb(222, 226, 230);
  }
}
</style>
